import CompanyHeader from './company/CompanyHeader';
import { ScrapbookEntry } from '@/types/scrapbook';
import { Building, Zap, ExternalLink, Users, Tag, Layers, Boxes, Edit3 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getEventLabels, mapCompanyToChanges } from '@/utils/eventLabels';
import { getScreenshotUrl } from '@/utils/companiesHelper';
import { createSafeExternalUrl } from '@/utils/urlUtils';

 type ContentType = 'Event' | 'Company';

interface CompanyCardProps {
  logoUrl: string;
  slug: string;
  description?: string;
  category: string;
  tags?: Array<{id: string, name: string, description?: string | null, keywords?: string[] | null} | string>;
  pricingPageUrl?: string;
  employees?: string;
  onClick?: () => void;
  onEditClick?: (slug: string) => void;
  scrapbookEntries: ScrapbookEntry[];
  onScrapbookUpdate?: () => void;
  beforeUrl?: string;
  afterUrl?: string;
  plansChanged?: boolean;
  priceIncreased?: boolean;
  priceDecreased?: boolean;
  priceAvailbilityChanged?: boolean;
  introDiscount?: boolean;
  displayChanged?: boolean;
  pricingMetrics?: any[]; // Add pricingMetrics property
  pages?: any[]; // Add pages property
}

const CompanyCard = ({
  logoUrl,
  slug,
  description,
  category,
  tags = [],
  pricingPageUrl,
  employees,
  onClick,
  onEditClick,
  scrapbookEntries,
  onScrapbookUpdate,
  beforeUrl,
  afterUrl,
  plansChanged,
  priceIncreased,
  priceDecreased,
  priceAvailbilityChanged: availabilityChanged,
  introDiscount,
  displayChanged,
  pricingMetrics = [],
  pages = []
}: CompanyCardProps) => {
  // Only log for specific companies to reduce noise
  if (slug === 'stripe' || slug === 'shopify' || slug === 'github') {
    console.log(`🏢 [CompanyCard] Company data for ${slug}:`, {
      slug,
      tags: Array.isArray(tags) ? tags : 'not an array'
    });
  }
  

  const contentType: ContentType = beforeUrl && afterUrl ? 'Event' : 'Company';
  
  // Check if company has multiple products/pages
  const hasMultipleProducts = Array.isArray(pages) && pages.length > 1;

  // Project functionality removed

  const changes = contentType === 'Event' ? mapCompanyToChanges({
    plansChanged,
    priceIncreased,
    priceDecreased,
    priceAvailbilityChanged: availabilityChanged,
    introDiscount,
    displayChanged
  }) : null;
  
  const eventLabels = changes ? getEventLabels(changes) : [];
  
  // Extract unique modalities from pricing metrics, but only include Credit, Usage, or License
  const allowedModalities = ['Credit', 'Usage', 'License'];
  const uniqueModalities = Array.from(new Set(
    pricingMetrics
      ?.filter(metric => metric.modality && allowedModalities.includes(metric.modality))
      .map(metric => metric.modality)
  ));

  return (
    <div
      className="bg-white/80 backdrop-blur-sm p-6 shadow-sm hover:shadow-md transition-shadow duration-200 border border-gray-200/50 hover:bg-gray-50/80 cursor-pointer relative flex flex-col min-h-[200px] rounded-lg"
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative w-12 h-12 flex-shrink-0">
            <img
              src={logoUrl}
              alt={`${slug} logo`}
              className="w-full h-full object-contain"
            />
            <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm border-2 border-white">
              {contentType === 'Event' ? (
                <Zap className="h-4 w-4 text-yellow-500" />
              ) : (
                <Building className="h-4 w-4 text-gray-500" />
              )}
            </div>
          </div>
          <h3 className="font-semibold text-lg text-gray-900 capitalize">
            {slug.replace(/-/g, ' ')}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {onEditClick && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEditClick(slug);
              }}
              className="h-8 w-8 p-0 hover:bg-blue-50"
            >
              <Edit3 className="h-4 w-4 text-blue-600" />
            </Button>
          )}
        </div>
      </div>

      <div className="w-full mt-4">
        {description && (
          <p className="text-sm text-gray-600">
            {description.length > 250 ? `${description.substring(0, 250)}...` : description}
          </p>
        )}
        {contentType === 'Event' && eventLabels.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {eventLabels.map((label, index) => (
              <Badge
                key={index}
                className={`flex items-center gap-1 ${label.className}`}
              >
                <label.icon className="h-3 w-3" />
                <span>{label.label}</span>
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="w-full mt-auto pt-4">
        {contentType === 'Company' && (
          <div className="flex flex-wrap gap-2 mt-2">
            {category && (
              <Badge variant="secondary" className="flex items-center gap-1 text-xs bg-gray-100 hover:bg-gray-200 transition-colors">
                <Building className="h-3 w-3 text-gray-500" />
                <span>{category}</span>
              </Badge>
            )}
            {employees && (
              <Badge variant="secondary" className="flex items-center gap-1 text-xs bg-blue-50 hover:bg-blue-100 transition-colors">
                <Users className="h-3 w-3 text-blue-500" />
                <span>{employees}</span>
              </Badge>
            )}
            {uniqueModalities && uniqueModalities.length > 0 && uniqueModalities.map((modality, index) => (
              <Badge 
                key={`modality-${index}`}
                variant="secondary" 
                className="flex items-center gap-1 text-xs bg-purple-50 hover:bg-purple-100 transition-colors"
              >
                <Layers className="h-3 w-3 text-purple-500" />
                <span>{modality}</span>
              </Badge>
            ))}
            {Array.isArray(pages) && pages.length > 1 && (
              <Badge 
                variant="secondary" 
                className="flex items-center gap-1 text-xs bg-green-50 hover:bg-green-100 transition-colors"
              >
                <Boxes className="h-3 w-3 text-green-500" />
                <span>Multiple Products ({pages.length})</span>
              </Badge>
            )}
            {/* Display tags - safely handle different tag structures */}
            {Array.isArray(tags) && tags.length > 0 && tags.map((tag, index) => {
              // Handle different tag structures
              let tagName = '';
              let tagId = '';
              
              if (typeof tag === 'string') {
                tagName = tag;
                tagId = tag;
              } else if (tag && typeof tag === 'object') {
                // Handle tag object with name property (standard tag object)
                if ('name' in tag && typeof tag.name === 'string') {
                  tagName = tag.name;
                  tagId = ('id' in tag && typeof tag.id === 'string') ? tag.id : tag.name;
                } else if ('tag' in tag && tag.tag && typeof tag.tag === 'object' && 'name' in tag.tag && typeof tag.tag.name === 'string') {
                  // Handle nested tag structure from company_tags view
                  tagName = tag.tag.name;
                  tagId = ('id' in tag.tag && typeof tag.tag.id === 'string') ? tag.tag.id : tag.tag.name;
                }
              }
              
              if (!tagName) return null;
              
              return (
                <Badge 
                  key={`tag-${tagId}-${index}`}
                  variant="secondary" 
                  className="flex items-center gap-1 text-xs bg-green-50 hover:bg-green-100 transition-colors text-green-700"
                >
                  <Tag className="h-3 w-3 text-green-500" />
                  <span>{tagName}</span>
                </Badge>
              );
            })}
            {pricingPageUrl && (
              <a 
                href={createSafeExternalUrl(pricingPageUrl)} 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-xs text-blue-600 hover:underline flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 hover:bg-blue-100 transition-colors"
              >
                <ExternalLink className="h-3 w-3" />
                <span>Pricing Page</span>
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CompanyCard;
